import React, { useState } from 'react';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { SmartCardProvider as DefaultSmartCardProvider } from '@atlaskit/link-provider';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import ConfluenceCreateIllustration from './assets/ConfluenceCreateIllustration.svg';
import { ObjectAwareSection } from './components/ObjectAwareSection';
import { InputBox } from './components/SmartCreateInput';
import { useVoiceInput } from './hooks/useVoiceInput';
import type { ObjectExamplesConfig } from './model/ObjectExamples';
import { useSmartStore } from './store/useSmartStore';

const styles = cssMap({
	content: {
		position: 'relative',
	},
	headingWrapper: {
		textAlign: 'center',
		verticalAlign: 'middle',
		paddingBlock: token('space.300'),
		paddingInline: token('space.200'),
	},
	illustration: {
		marginBottom: token('space.075'),
		transition: 'opacity 0.2s ease-in-out',
	},
	promptContainer: {
		marginTop: token('space.600'),
	},
});

type ErrorBoundaryProps = {
	children: React.ReactNode;
};

type SmartCreatePromptProps = {
	handleCreateContent?: () => void;
	SmartCardProvider?: typeof DefaultSmartCardProvider;
	ErrorBoundary?: React.ComponentType<ErrorBoundaryProps>;
	examples?: ObjectExamplesConfig;
};

const DefaultErrorBoundary = ({ children }: ErrorBoundaryProps) => <div>{children}</div>;

export const SmartCreatePrompt = ({
	handleCreateContent = () => {},
	SmartCardProvider = DefaultSmartCardProvider,
	ErrorBoundary = DefaultErrorBoundary,
	examples,
}: SmartCreatePromptProps) => {
	const { prompt, contentTypes, inputMethod } = useSmartStore();
	const [isObjectAwareSectionVisible, setIsObjectAwareSectionVisible] = useState(true);

	// Handle voice input method
	useVoiceInput({
		inputMethod,
	});

	const handleObjectSelect = () => {
		setIsObjectAwareSectionVisible(false);
	};

	return (
		<Flex
			direction="column"
			testId="smart-create-prompt"
			justifyContent="start"
			alignItems="center"
		>
			<Flex direction="column" alignItems="center" xcss={styles.content}>
				<Box as="img" src={ConfluenceCreateIllustration} alt="" xcss={styles.illustration} />
				<Heading size="xxlarge">What will you create today?</Heading>
				<Flex xcss={styles.promptContainer}>
					<InputBox
						onCreate={handleCreateContent}
						prompt={prompt}
						contentTypes={contentTypes}
						inputMethod={inputMethod}
						SmartCardProvider={SmartCardProvider}
						ErrorBoundary={ErrorBoundary}
					/>
				</Flex>
				{examples && isObjectAwareSectionVisible && (
					<ObjectAwareSection examples={examples} onSelect={handleObjectSelect} />
				)}
			</Flex>
		</Flex>
	);
};
